import { useState, useEffect, useMemo } from 'react';
import { formatPhone } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Cake, Gift, Search, ChevronLeft, Mail, Phone } from 'lucide-react';
import { dashboardAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Full birthdays page — everything the compact dashboard widget shows plus
 * search, window selector, and grouping by "Today / This Week / Later".
 */
export default function BirthdaysPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dashboardAPI.upcomingBirthdays(days)
      .then((r) => { if (!cancelled) setItems(r.data.birthdays || []); })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || 'Failed to load birthdays');
        setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((b) => (
      `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().includes(q)
      || (b.agency_name || '').toLowerCase().includes(q)
      || (b.npn || '').toLowerCase().includes(q)
      || (b.email || '').toLowerCase().includes(q)
    ));
  }, [items, search]);

  const groups = useMemo(() => {
    const today = [];
    const thisWeek = [];
    const later = [];
    for (const b of filtered) {
      if (b.days_until === 0) today.push(b);
      else if (b.days_until <= 7) thisWeek.push(b);
      else later.push(b);
    }
    return { today, thisWeek, later };
  }, [filtered]);

  const friendlyDate = (b) => {
    if (b.days_until === 0) return 'Today';
    if (b.days_until === 1) return 'Tomorrow';
    if (b.days_until <= 7) return `In ${b.days_until} days`;
    const next = new Date();
    next.setDate(next.getDate() + b.days_until);
    return next.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const openItem = (b) => {
    if (b.type === 'agent') navigate(`/agents/${b.npn}`);
    else navigate(`/clients/${b.id}`);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="birthdays-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="birthdays-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Cake className="w-6 h-6 text-pink-600" />
            <h1 className="text-2xl font-bold">Upcoming Birthdays</h1>
          </div>
          <p className="text-sm text-slate-600">
            Agents and clients with a birthday coming up. Click any card to open their profile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, agency, NPN…"
              className="pl-8 w-64"
              data-testid="birthdays-search"
            />
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40" data-testid="birthdays-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Next 7 days</SelectItem>
              <SelectItem value="14">Next 14 days</SelectItem>
              <SelectItem value="30">Next 30 days</SelectItem>
              <SelectItem value="60">Next 60 days</SelectItem>
              <SelectItem value="90">Next 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-slate-400">Loading…</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-slate-500">
            <Cake className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            {search
              ? <>No birthdays match &ldquo;{search}&rdquo;.</>
              : <>No birthdays in the next {days} days.</>}
          </CardContent>
        </Card>
      ) : (
        <>
          {groups.today.length > 0 && <Section title="🎂 Today" tone="pink" items={groups.today} onOpen={openItem} friendlyDate={friendlyDate} />}
          {groups.thisWeek.length > 0 && <Section title="This Week" tone="amber" items={groups.thisWeek} onOpen={openItem} friendlyDate={friendlyDate} />}
          {groups.later.length > 0 && <Section title="Later" tone="slate" items={groups.later} onOpen={openItem} friendlyDate={friendlyDate} />}
        </>
      )}
    </div>
  );
}

function Section({ title, tone, items, onOpen, friendlyDate }) {
  const toneClasses = {
    pink: 'border-pink-200 bg-pink-50/40',
    amber: 'border-amber-200 bg-amber-50/40',
    slate: 'border-slate-200',
  }[tone] || 'border-slate-200';

  return (
    <Card className={toneClasses}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="outline">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((b) => (
          <button
            key={`${b.type}-${b.id || b.npn}`}
            type="button"
            onClick={() => onOpen(b)}
            data-testid={`birthday-card-${b.type}-${b.id || b.npn}`}
            className="text-left border rounded-lg p-3 bg-white hover:shadow transition flex gap-3 items-start"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-amber-200 flex items-center justify-center text-pink-700 shrink-0">
              {b.is_today ? <Gift className="h-6 w-6" /> : <span className="text-lg font-semibold">{(b.first_name?.[0] || '?').toUpperCase()}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                {b.first_name} {b.last_name}
                {b.type !== 'agent' && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-teal-300 text-teal-700">
                    {b.type === 'dependent' ? 'Dependent' : 'Client'}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate">
                Turning {b.turning_age}{b.agency_name ? ` · ${b.agency_name}` : ''}
              </div>
              <div className="text-xs text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                <Badge className={b.is_today ? 'bg-pink-600 text-white' : 'bg-slate-100 text-slate-700'}>
                  {friendlyDate(b)}
                </Badge>
                {b.email && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Mail className="w-3 h-3" /> <span className="truncate max-w-[140px]">{b.email}</span>
                  </span>
                )}
                {b.phone && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Phone className="w-3 h-3" /> {formatPhone(b.phone)}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}
